import { useEffect, useMemo, useState, Suspense, useRef } from "react";
import { Await, useRouteLoaderData } from "react-router-dom";
import { Button, Stack, Modal, Form, Container } from "react-bootstrap";
import { axiosInstance } from "../../auth/Auth";
import ErrorResponse from "../ErrorResponse";
import LoadingIndicator from "../LoadingIndicator";
import ClocksModal from "../ClocksModal";
import RATooltip from "../RATooltip";
import BackToTopButton from "../BackToTopButton";
import SongHistoryHeader from "./SongHistoryHeader";
import SongHistoryRow from "./SongHistoryRow";
import { useVirtualizer } from "@tanstack/react-virtual";
import EmptyRow from "../EmptyRow";
import { useResolvedModules } from "../HelperHooks";
import { ClockFill } from "react-bootstrap-icons";

export default function SongHistoryModal({ show, onHide, song, radioId }) {
    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState(null);
    const [data, setData] = useState(null);
    const [months, setMonths] = useState(6);
    const { competitorRadios } = useRouteLoaderData("root");
    const [selectedPlay, setSelectedPlay] = useState(null);
    const tableContainerRef = useRef();
    const [showEmptyDates, setshowEmptyDates] = useState(false);

    const resolvedModules = useResolvedModules();

    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);

    useEffect(() => {
        async function FetchData() {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`items/${song.item_id}/${radioId}/song_history/?months=${months}`);
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        }
        if (song && radioId && show && musicEnabled) FetchData();
    }, [show, song, radioId, months, musicEnabled]);

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data.dates.sort((a, b) => new Date(b.date) - new Date(a.date));
            if (!showEmptyDates) {
                filteredData = filteredData.filter(x => x.plays.length > 0);
            }
            return filteredData;
        }
    }, [showEmptyDates, data]);

    const virtualizer = useVirtualizer({
        count: dataMemo?.length || 1,
        estimateSize: () => 30,
        overscan: 30,
        getScrollElement: () => tableContainerRef.current,
    });

    return (
        <>
            <Modal show={show && musicEnabled} onHide={onHide} size="xl" centered onExited={() => setMonths(6)} scrollable>
                <Modal.Header closeButton className=" py-1 py-xl-3">
                    <Modal.Title className="d-flex column-gap-1 text-uppercase">
                        Song History
                        <RATooltip>
                            <Stack>
                                <p> Click the hour you are interested in and get sent to Clocks (you need to click the Clock icon).</p>
                                <p className="mb-0">The colors indicate the quarter of the hour where the song was played:</p>
                                <Stack className=" ms-1">
                                    <span>
                                        <ClockFill className="song-history-quarter0 bi bi-clock-fill" /> First quarter of hour
                                    </span>
                                    <span>
                                        <ClockFill className="song-history-quarter1 bi bi-clock-fill" /> Second quarter of hour
                                    </span>
                                    <span>
                                        <ClockFill className="song-history-quarter2 bi bi-clock-fill" /> Third quarter of hour
                                    </span>
                                    <span>
                                        <ClockFill className="song-history-quarter3 bi bi-clock-fill" /> Fourth quarter of hour
                                    </span>
                                </Stack>
                            </Stack>
                        </RATooltip>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column pt-1 row-gap-2">
                    {loading ? (
                        <LoadingIndicator width={75} />
                    ) : errorCode ? (
                        <ErrorResponse errorCode={errorCode} />
                    ) : (
                        dataMemo && (
                            <>
                                <BackToTopButton container={tableContainerRef} />
                                <Stack direction="horizontal" className="flex-wrap" gap={1}>
                                    <Stack direction="horizontal" gap={1} className="flex-wrap">
                                        <span>Song history for </span>
                                        <span className="fw-bold">{`${song?.artist} - ${song?.title}`}</span>
                                        <Suspense>
                                            <Await resolve={competitorRadios}>
                                                {resolvedRadios => (
                                                    <span>
                                                        on <span className="fw-bold">{resolvedRadios.data.find(x => x.radio_id === radioId)?.radio_name}</span>
                                                    </span>
                                                )}
                                            </Await>
                                        </Suspense>
                                    </Stack>
                                    <Form.Switch
                                        label="Show Empty Dates"
                                        className=" ms-xl-auto align-content-center"
                                        inline
                                        disabled={!months}
                                        id={`song-history-empty-dates-switch`}
                                        checked={showEmptyDates}
                                        onChange={e => setshowEmptyDates(e.target.checked)}
                                    />
                                </Stack>
                                <div className=" overflow-auto" ref={tableContainerRef}>
                                    <SongHistoryHeader />
                                    <Container fluid className="position-relative" style={{ height: virtualizer.getTotalSize() }}>
                                        {virtualizer.getVirtualItems().map(virtualRow => {
                                            const item = dataMemo[virtualRow.index];
                                            if (!item) {
                                                return <EmptyRow key={virtualRow.key} text="No History" />;
                                            }
                                            return (
                                                <SongHistoryRow
                                                    key={virtualRow.key}
                                                    item={item}
                                                    virtualRow={virtualRow}
                                                    virtualizer={virtualizer}
                                                    setSelectedPlay={setSelectedPlay}
                                                />
                                            );
                                        })}
                                    </Container>
                                </div>
                                <Button
                                    variant={"ra-yellow"}
                                    onClick={() => {
                                        setMonths(months ? 0 : 6);
                                        setshowEmptyDates(false);
                                    }}
                                    className="d-flex align-self-center text-uppercase px-5 py-1">
                                    {months ? "Show full history" : "Show latest 6 months"}
                                </Button>
                            </>
                        )
                    )}
                </Modal.Body>
            </Modal>
            <ClocksModal
                show={!!selectedPlay}
                handleClose={() => setSelectedPlay(null)}
                radioId={radioId}
                date={selectedPlay?.date}
                hour={selectedPlay?.hour}
            />
        </>
    );
}
